import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as gridUtils from "grid/utils";
import * as http from "utils/http";
import * as responses from "utils/responses";
import {
  AMOUNT_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
  DEFAULT_COL_DEF,
  ACTIONS_COL_DEF,
  TEXT_FILTER_COL_DEF,
} from "grid/columns";
import * as columns from "grid/columns";
import { GridController } from "grid/grid_controller";

interface ScheduledPayment {
  account_numbers: string[];
  delete_message: string;
  delete_path: string;
  id: string;
  invoice_numbers: string[];
  next_payment_date: string;
  payment_amount: number;
  payment_channel: string;
}

export default class extends GridController<ScheduledPayment> {
  static values = Object.assign(super.values, {
    isPaymentChannelEnabled: Boolean,
  });

  declare readonly isPaymentChannelEnabledValue: boolean;

  override buildGrid() {
    const { i18nJson, paymentChannelsJson, paginationPageSize } = this.gridTarget.dataset;

    const colDefs: AgGrid.ColDef[] = [
      ...(this.isPaymentChannelEnabledValue
        ? [
            {
              field: "payment_channel",
              ...DEFAULT_COL_DEF,
              sortable: true,
              ...columns.buildEnumColDef(JSON.parse(paymentChannelsJson)),
              ...columns.buildEnumFilterColDef(JSON.parse(paymentChannelsJson)),
            },
          ]
        : []),
      { field: "account_numbers", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, tooltipField: "account_numbers" },
      { field: "invoice_numbers", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, tooltipField: "invoice_numbers" },
      { field: "payment_amount", ...AMOUNT_COL_DEF, ...AMOUNT_FILTER_COL_DEF },
      { field: "next_payment_date", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true, sort: "asc" },
      { ...ACTIONS_COL_DEF, minWidth: 70, maxWidth: 90 },
    ];

    const options: AgGrid.GridOptions<ScheduledPayment> = {
      ...gridUtils.DEFAULT_GRID_OPTIONS,
      // override defaults
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n: JSON.parse(i18nJson) }),
      rowModelType: "clientSide",
      getRowId: (params) => params.data.id,
    };
    const gridApi = AgGrid.createGrid(this.gridTarget, options);

    const dataSource = new datasource.Datasource({ callbacks: {}, limit: paginationPageSize });

    dataSource.getRows({
      context: null,
      endRow: 1,
      failCallback: () => {},
      filterModel: gridApi.getFilterModel(),
      sortModel: gridApi.getState().sort.sortModel,
      startRow: 1,
      successCallback: (records) => {
        gridApi.setGridOption("loading", false);
        gridApi.setGridOption("rowData", records);
      },
    });

    return gridApi;
  }

  async handleDelete(event: CustomEvent<{ url: string }>) {
    const response = await http.httpDelete(event.detail.url);
    if (response.ok) {
      const id = event.detail.url.split("/").pop();
      const rowNode = this.gridApi.getRowNode(id);
      if (rowNode) {
        this.gridApi.applyTransaction({ remove: [rowNode.data] });
      }
    } else {
      await responses.notifyAboutFailure(response);
    }
  }
}
